import { useMutation, useQueries } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Modal,
  Radio,
  Show
} from "shared/components";
import { Card } from "shared/components/Card";
import InfoIcon from "shared/icons/status-icons/info-icon.svg?react";
import { Application, MergeApplicationsError } from "types/application";
import { useNavigate } from "react-router-dom";

import { getErrorMessage } from "utils/error";
import { Loader } from "shared/components/Loader";
import { toast } from "react-toastify";
import { getFullName } from "shared/utils/get-full-name";
import { cx } from "shared/utils";
import { useState } from "react";
import { getApplicantRole } from "utils/getApplicantRole";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { AxiosError } from "axios";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { getApplicantDisplayName } from "shared/utils/get-applicant-display-name";

const getApplication = async (id: string) => {
  return instance<Application>(`/portal/v1/applications/${id}`).then(
    ({ data }) => data
  );
};

// Maps a merge validation error to a user-facing message. Order matters: the
// first matching error wins, so more specific messages should come first.
const MERGE_ERROR_MESSAGES: { key: string; message: string }[] = [
  {
    key: "merging_applications_should_not_have_association_to_at_least_one_customer",
    message:
      "This application already has an active applicant with the same contact details. Please cancel the duplicate applicant before attempting to merge these application groups."
  },
  {
    key: "not_mergeable_status",
    message:
      "The target application must be in 'Partially submitted', 'Submitted' or 'In progress' state"
  },
  {
    key: "application_on_hold",
    message:
      "Applications that are on hold can't be merged. Resume application and try again."
  }
];

type ApplicationUser =
  | Application["applicants"][number]
  | NonNullable<Application["pending_magic_links"]>[number];

const ApplicationOptionCard = ({
  application,
  isSelected,
  onSelect
}: {
  application: Application;
  isSelected: boolean;
  onSelect: () => void;
}) => {
  const dictionary = useUnitsPropertiesDictionary();

  const applicantNames = application.applicants
    .map((el) =>
      getFullName({
        firstName: el.first_name,
        lastName: el.last_name,
        middleName: el.middle_name,
        short: true
      })
    )
    .join(", ");

  return (
    <Card className={cx("px-6 py-4", isSelected ? "border-primary-100" : null)}>
      <div className="flex justify-between items-center border-b border-alpha-40">
        <div className="flex flex-col">
          <p className="text-sm text-content-400 mb-2">Application group</p>
          <h3
            className="truncate text-base text-content-1000 font-bold pb-4 max-w-[400px]"
            title={applicantNames}
          >
            {applicantNames}
          </h3>
        </div>
        <Radio
          name="criteriaEnabled"
          id={String(application.id)}
          checked={isSelected}
          onChange={onSelect}
        />
      </div>

      <div className="space-y-4 mt-4">
        <div className="space-y-2">
          <p className="text-xs text-content-400 font-normal">
            {dictionary.Property}
          </p>
          <p className="text-sm text-content-1000 font-medium">
            {application.property.name}
          </p>
        </div>

        <div className="space-y-2">
          <p className="text-xs text-content-400 font-normal">
            {dictionary.Unit}
          </p>
          <p className="text-sm text-content-1000 font-medium">
            {application.unit.title}
          </p>
        </div>

        <div className="space-y-2">
          <p className="text-xs text-content-400 font-normal">
            Screening settings
          </p>
          <p className="text-sm text-content-1000 font-medium">
            {application.verification_template?.name || ""}
          </p>
        </div>
      </div>
    </Card>
  );
};

const MergedApplicantsSummary = ({ users }: { users: ApplicationUser[] }) => {
  return (
    <Card className="px-6 py-4">
      <p className="text-sm text-content-400 mb-2">Application group</p>
      <h3 className="truncate text-base text-content-1000 font-bold pb-4 border-b border-alpha-100">
        {users.map(getApplicantDisplayName).join(", ")}
      </h3>

      <ul className="mt-4">
        {users.map((applicant) => (
          <li key={applicant.email} className="mb-4">
            <p className="text-sm text-content-1000 mb-2 font-medium">
              {getApplicantDisplayName(applicant)}
            </p>
            <p className="text-xs text-content-400">
              {getApplicantRole(applicant.role)}
            </p>
          </li>
        ))}
      </ul>
    </Card>
  );
};

export const MergeApplicantsModal = ({
  isOpen,
  onClose,
  selectedApplicants
}: {
  isOpen: boolean;
  onClose: () => void;
  selectedApplicants: string[];
}) => {
  const navigate = useNavigate();
  const [isConfirmStep, setIsConfirmStep] = useState<boolean>(false);
  const [selectedApplicationId, setSelectedApplicationId] = useState<
    string | undefined
  >();
  const applicationsMeta = useQueries({
    queries: selectedApplicants.map((applicantId) => ({
      queryKey: ["application", applicantId],
      queryFn: () => getApplication(applicantId)
    })),
    combine: (results) => {
      return {
        data: results.reduce((acc: Application[], result) => {
          if (result.data) acc.push(result.data);

          return acc;
        }, []),
        pending: results.some((result) => result.isPending),
        loading: results.some((result) => result.isLoading)
      };
    }
  });

  const { mutate, isPending } = useMutation({
    mutationFn: () => {
      return instance.post("/portal/v1/applications/merge", {
        target_application_id: selectedApplicationId,
        application_ids: applicationsMeta.data.reduce((acc, application) => {
          if (application.id !== Number(selectedApplicationId))
            acc.push(application.id);

          return acc;
        }, [] as number[])
      });
    },
    mutationKey: ["application", selectedApplicationId],
    onSuccess: () => {
      toast.success("Application successfully merged");
      navigate(`/applications/${selectedApplicationId}`);
    },
    onError: (error: AxiosError<MergeApplicationsError>) => {
      const allErrors =
        error.response?.data.validation_errors.flatMap((el) => el.errors) ?? [];

      const matchedMessage = MERGE_ERROR_MESSAGES.find(({ key }) =>
        allErrors.includes(key)
      )?.message;

      toast.error(matchedMessage ?? getErrorMessage(error));
    }
  });

  const allUsers = applicationsMeta.data.flatMap((applicant) => [
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    ...(applicant.applicants || []),
    ...(applicant.pending_magic_links || [])
  ]);

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      panelClassName="max-w-[600px] max-h-[640px] w-full min-h-[300px] flex flex-col h-full"
    >
      <div className="p-6 border-b border-alpha-100 flex flex-col">
        <div className="pb-2 flex justify-between items-center">
          <h1 className="text-xl text-content-1000 font-bold">
            {isConfirmStep
              ? "Confirm merging applications"
              : "Select the application to merge others into"}
          </h1>
          <IconButton
            onClick={onClose}
            className="ml-auto"
            HugeIcon={Cancel01Icon}
            size={IconButtonSize.BASE}
            variant={IconButtonVariant.TEXT_BASE}
          />
        </div>

        <Show if={!isConfirmStep}>
          <p className="text-sm text-content-500 font-normal">
            All applicants and guarantors will be merged into one application.
            The new application will use the screening settings from the
            selected application. Individual applications will keep the original
            set of requested information, but any missing reports will be added
          </p>
        </Show>
      </div>

      <Show if={isConfirmStep}>
        <div className="bg-warning-50 grow-0 px-6 py-4 flex gap-4 items-center border-b border-alpha-100">
          <InfoIcon className="text-warning-500 h-5 w-5" />
          <p className="text-sm text-warning-700 font-medium">
            Merging applications may change the screening result, so make sure
            everything looks right
          </p>
        </div>
      </Show>

      <div className="h-full flex flex-col grow-1 overflow-y-auto px-6 py-4 bg-background-900 gap-4">
        <Show if={applicationsMeta.loading}>
          <div className="w-full flex items-center justify-center h-full">
            <Loader />
          </div>
        </Show>

        <Show if={!applicationsMeta.loading && !isConfirmStep}>
          {applicationsMeta.data.map((application) => (
            <ApplicationOptionCard
              key={application.id}
              application={application}
              isSelected={selectedApplicationId === String(application.id)}
              onSelect={() => setSelectedApplicationId(String(application.id))}
            />
          ))}
        </Show>

        <Show if={!applicationsMeta.loading && isConfirmStep}>
          <MergedApplicantsSummary users={allUsers} />
        </Show>
      </div>
      <div className="flex h-[72px] justify-end px-6 py-4 border-t border-alpha-100 gap-2">
        <Button
          size={ButtonSize.SM}
          onClick={() => {
            if (isConfirmStep) {
              setIsConfirmStep(false);
              setSelectedApplicationId(undefined);
            } else onClose();
          }}
        >
          Back
        </Button>
        <Button
          variant={ButtonVariant.PRIMARY}
          onClick={() => {
            if (isConfirmStep) {
              if (!isPending) mutate();
            } else setIsConfirmStep(true);
          }}
          loading={isPending}
          size={ButtonSize.SM}
          disabled={
            applicationsMeta.loading || isPending || !selectedApplicationId
          }
        >
          {isConfirmStep ? "Complete merge" : "Continue"}
        </Button>
      </div>
    </Modal>
  );
};
